/**
 * The analyst.
 *
 * Questions go to the server, which holds the key and assembles the portfolio context
 * from the same snapshot the dashboard renders. The browser never sees a credential and
 * the analyst can never cite a figure the screen does not show.
 */

import { useRef, useState } from "react";
import { Notice, Section } from "../components/Chrome";
import { askAnalyst, type ChatMessage } from "../lib/api";

const SUGGESTIONS = [
  "Which vendor should worry me most, and why?",
  "How much are we leaving on the table in service credits?",
  "Which metrics can't you independently verify, and what would it take?",
  "What should I raise in the Continuum QBR next week?",
];

export function AnalystView({ tenant }: { tenant: string | null }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  async function send(question: string) {
    const trimmed = question.trim();
    if (!trimmed || pending) return;

    const next: ChatMessage[] = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setPending(true);
    setError(null);

    try {
      const answer = await askAnalyst(next, tenant);
      setMessages([...next, { role: "assistant", content: answer.content }]);
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : "The analyst could not be reached.",
      );
    } finally {
      setPending(false);
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: "smooth" }));
    }
  }

  return (
    <div className="fade-in">
      <Section
        title="Analyst"
        subtitle="Grounded in the portfolio above: both figures for every service level, the contract terms behind them, and which metrics have no independent source."
      >
        {messages.length === 0 && (
          <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
            {SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => send(suggestion)}
                className="card card--interactive"
                style={{
                  padding: "12px 14px",
                  textAlign: "left",
                  color: "var(--parchment)",
                  fontSize: 13,
                  background: "var(--panel)",
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: "grid", gap: 12, marginBottom: 14 }}>
          {messages.map((message, index) => (
            <div
              key={index}
              className="card"
              style={{
                padding: "14px 16px",
                borderColor: message.role === "user" ? "var(--line)" : "rgba(200,155,60,0.35)",
                background: message.role === "user" ? "transparent" : "var(--panel)",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: "0.07em",
                  color: message.role === "user" ? "var(--slate)" : "var(--gold)",
                  marginBottom: 6,
                }}
              >
                {message.role === "user" ? "YOU" : "ACTUOVIOUS"}
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                {message.content}
              </div>
            </div>
          ))}
          {pending && (
            <div style={{ fontSize: 12.5, color: "var(--slate)" }}>Reading the portfolio…</div>
          )}
          <div ref={endRef} />
        </div>

        {error && (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="warn">{error}</Notice>
          </div>
        )}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void send(input);
          }}
          style={{ display: "flex", gap: 8 }}
        >
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Ask about any vendor, service level, or figure on screen"
            aria-label="Ask the analyst"
            style={{
              flex: 1,
              background: "transparent",
              border: "1px solid var(--line-strong)",
              borderRadius: 8,
              padding: "11px 13px",
              color: "var(--parchment)",
              fontSize: 13.5,
              fontFamily: "var(--sans)",
            }}
          />
          <button
            type="submit"
            disabled={pending || !input.trim()}
            style={{
              background: "var(--gold)",
              border: "none",
              borderRadius: 8,
              padding: "0 20px",
              color: "var(--ink)",
              fontSize: 13,
              fontWeight: 600,
              opacity: pending || !input.trim() ? 0.5 : 1,
            }}
          >
            Ask
          </button>
        </form>
      </Section>
    </div>
  );
}
